import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../config/firebase';
import { trackEvent } from '../config/analytics';
import * as Sentry from '@sentry/react';
import { useAuth } from '../contexts/AuthContext';

// Admin/Free Access Whitelist
const FREE_ACCESS_EMAILS = [
  'Alex@elitevisiongroup.com',
  'alex@elitevisiongroup.com',
  'stro.alexander@gmail.com'
];

// Functions that need direct HTTP calls (for CORS handling)
const HTTP_FUNCTIONS = ['runAudit', 'createSubscriptionCheckout', 'createPortalSession'];
const FUNCTIONS_BASE_URL = 'https://us-central1-accessshield-ai.cloudfunctions.net';

export const useCloudFunction = (functionName) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const hasAccess = () => {
    if (!currentUser) return false;
    // Check if user email is in whitelist (case-insensitive)
    return FREE_ACCESS_EMAILS.some(email =>
      email.toLowerCase() === currentUser.email?.toLowerCase()
    );
  };

  const execute = async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      console.log('[useCloudFunction] Calling function:', functionName);
      console.log('[useCloudFunction] Current user:', currentUser?.uid);

      // Add admin flag if user has free access
      const enhancedParams = hasAccess()
        ? { ...params, _adminAccess: true }
        : params;

      let resultData;

      // Use direct HTTP call for functions that need CORS handling
      if (HTTP_FUNCTIONS.includes(functionName)) {
        console.log('[useCloudFunction] Using direct HTTP call for:', functionName);

        // Get Firebase ID token
        const idToken = await auth.currentUser.getIdToken();

        const response = await fetch(`${FUNCTIONS_BASE_URL}/${functionName}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify(enhancedParams)
        });

        const responseData = await response.json();

        if (!response.ok) {
          throw new Error(responseData.error || `HTTP ${response.status}`);
        }

        resultData = responseData.result;
        console.log('[useCloudFunction] HTTP call SUCCESS!');
      } else {
        // Use Firebase callable for other functions
        console.log('[useCloudFunction] Using httpsCallable for:', functionName);
        const callable = httpsCallable(functions, functionName);
        const result = await callable(enhancedParams);
        resultData = result.data;
        console.log('[useCloudFunction] Callable SUCCESS!');
      }

      setData(resultData);
      
      trackEvent('cloud_function_call', {
        function: functionName,
        success: true,
      });
      
      return resultData;
    } catch (err) {
      console.error(`Error calling ${functionName}:`, err);
      setError(err.message);
      
      Sentry.captureException(err, {
        contexts: {
          cloudFunction: {
            name: functionName,
            params: params,
          }
        }
      });
      
      trackEvent('cloud_function_error', {
        function: functionName,
        error: err.message,
      });
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setLoading(false);
    setError(null);
    setData(null);
  };

  return { execute, loading, error, data, reset, hasAccess: hasAccess() };
};